import React, { useState, useRef } from 'react'
import Slider from 'react-slick'
import {
  Card,
  Typography,
  CardActionArea,
  Dialog,
  DialogContent,
  IconButton,
  Box,
  useMediaQuery,
  CircularProgress,
} from '@mui/material'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import ClearIcon from '@mui/icons-material/Clear'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import { IMAGE_BASE_PATH_DBRADC } from '../../Config/Config'
import { useTheme } from '@mui/material/styles'

const TestimonialVideo = ({ videos, title }) => {
  const [open, setOpen] = useState(false)
  const [currentVideo, setCurrentVideo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [muted, setMuted] = useState(true)
  const videoRef = useRef(null)
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'))

  const sliderSettings = {
    dots: false, // Enable dots for navigation
    infinite: true, // Allow infinite looping
    speed: 400, // Slightly increase transition speed for smoother animations
    slidesToShow: isSmallScreen ? 2 : isMediumScreen ? 2 : 4, // Adjust slides based on screen size
    slidesToScroll: 1, // Scroll one slide at a time for better control
    autoplay: true, // Enable autoplay
    autoplaySpeed: 1000, // Faster autoplay transition
    arrows: false, // Enable navigation arrows for desktop users
    swipeToSlide: true, // Optimize touch interactions for mobile devices
    lazyLoad: 'ondemand',
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 3, slidesToScroll: 1 } }, // Tablet settings
      { breakpoint: 768, settings: { slidesToShow: 2, slidesToScroll: 1 } }, // Medium screens
      { breakpoint: 480, settings: { slidesToShow: 1, slidesToScroll: 1 } }, // Mobile screens
    ],
  }

  const handleClick = (video) => {
    setCurrentVideo(video)
    setOpen(true)
    setLoading(true)
  }

  const handleClose = () => {
    if (videoRef.current) videoRef.current.pause()
    setOpen(false)
    setCurrentVideo(null)
    setLoading(false)
  }

  const handleVideoLoaded = () => setLoading(false)

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted
      setMuted(!muted)
    }
  }

  return (
    <Box className="container mt-4">
      <Typography variant="h4" sx={{ textAlign: 'center', py: 4 }}>
        {title}
      </Typography>

      <Slider {...sliderSettings}>
        {videos?.map((video, index) => (
          <Box key={index} sx={{ px: 0.5 }}>
            <Card sx={{ borderRadius: '10px', maxWidth: 245, mx: 'auto' }}>
              <CardActionArea onClick={() => handleClick(video)}>
                <Box sx={{ position: 'relative' }}>
                  <video
                    src={IMAGE_BASE_PATH_DBRADC + video.media_url}
                    width="100%"
                    height="340px"
                    muted
                    loop
                    playsInline
                    style={{ objectFit: 'cover', borderRadius: '10px' }}
                  />

                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                    }}
                    aria-label="Play video"
                  >
                    <PlayArrowIcon sx={{ fontSize: 25 }} />
                  </IconButton>
                </Box>
              </CardActionArea>
            </Card>
          </Box>
        ))}
      </Slider>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            bgcolor: 'black',
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0,0,0,0.7)',
                zIndex: 1,
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          )}

          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.1)',
              },
              zIndex: 2,
              transition: 'all 0.2s',
            }}
          >
            <ClearIcon />
          </IconButton>

          <IconButton
            onClick={toggleMute}
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.1)',
              },
              zIndex: 2,
              transition: 'all 0.2s',
            }}
          >
            {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
          </IconButton>

          {currentVideo && (
            <Box
              sx={{
                width: '100%',
                paddingTop: isSmallScreen ? '56.25%' : '50%',
                position: 'relative',
              }}
            >
              <video
                ref={videoRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  outline: 'none',
                }}
                controls
                autoPlay
                muted={muted}
                onLoadedData={handleVideoLoaded}
              >
                <source
                  src={IMAGE_BASE_PATH_DBRADC + currentVideo.media_url}
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default TestimonialVideo
